import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { bookingApi, calendarApi } from '../api';
import {
  Empty, ErrorNote, Modal, Spinner, dateOf, money, th, timeOf, useLoader,
} from '../components/common';
import { BedPicker, TherapistPicker } from '../components/pickers';
import { IconCalendar, IconGuest, IconPin } from '../components/icons';

const TABS = [
  { key: 'draft', label: 'รอยืนยัน' },
  { key: 'pending_review', label: 'กำลังชำระเงิน' },
  { key: 'booked', label: 'ยืนยันแล้ว' },
  { key: 'cancelled', label: 'ยกเลิก' },
  { key: 'all', label: 'ทั้งหมด' },
];

const SITE_URL = (
  process.env.REACT_APP_PUBLIC_SITE_URL || `${window.location.origin}/spa`
).replace(/\/+$/, '');

const BADGE = {
  draft: 'badge-purple',
  pending_review: 'badge-grey',
  booked: 'badge-green',
  cancelled: 'badge-grey',
};

/**
 * เว็บจอง — what the customer site sent in.
 *
 * A paid online request is filed as a draft until reception assigns its
 * therapist and bed. Payment has already committed the shop to the booking,
 * so allocation is the only action offered here.
 */
export default function BookingWebsite() {
  const location = useLocation();
  const redirectedWarning = location.state?.onlineAllocationWarning;
  const [tab, setTab] = useState('draft');
  const [assigning, setAssigning] = useState(null);
  const [notice, setNotice] = useState(() => redirectedWarning || null);
  const noticeReconciled = useRef(false);

  const list = useLoader(() => bookingApi.requests(tab), [tab]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 8_000);
    return () => clearTimeout(timer);
  }, [notice]);

  const rows = list.data?.requests || [];
  const pending = list.data?.pending || 0;

  // A redirect warning is state carried over from the calendar click. If the
  // queue has since been resolved, do not leave a ghost "1 remaining" toast on
  // an empty page. Recheck once because Beam expiry reconciliation can finish
  // just after the first list request.
  useEffect(() => {
    if (!notice || !list.data || rows.length > 0 || noticeReconciled.current) return;
    noticeReconciled.current = true;
    bookingApi.onlineGate().then((gate) => {
      if (!gate.pendingToday) setNotice(null);
      else list.reload();
    }).catch(() => setNotice(null));
  }, [notice, list.data, rows.length, list]);

  return (
    <>
      {notice && (
        <div className="allocation-toast" role="alert" aria-live="assertive">
          <button
            type="button"
            className="allocation-toast-close"
            aria-label="ปิด"
            onClick={() => setNotice(null)}
          >
            ✕
          </button>
          <strong>จัดสรรเว็บจองของวันนี้ก่อน</strong>
          <span>{notice.message}</span>
          <small>คงเหลือ {notice.pendingToday} รายการ</small>
        </div>
      )}

      {/* Their เว็บจอง heads with the standard 30/600 page title; ours had none
          at all, which left the screen starting on a bare row of chips. */}
      <h1 className="page-title">เว็บจอง</h1>
      <div className="toolbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`chip${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'draft' && tab === 'draft' && pending > 0 ? ` · ${pending}` : ''}
          </button>
        ))}
        <a className="btn btn-outline" href={SITE_URL} target="_blank" rel="noreferrer"
           style={{ marginLeft: 'auto', textDecoration: 'none' }}>
          เปิดหน้าเว็บลูกค้า
        </a>
      </div>

      <div className="card">
        {list.loading ? <Spinner />
          : list.error ? <ErrorNote error={list.error} />
          : !rows.length ? <Empty icon="online" message={tab === 'draft' ? 'ไม่มีคำขอจองใหม่' : 'ไม่พบข้อมูล'} />
          : (
            <table className="data">
              <thead>
                <tr>
                  <th>รหัสจอง</th>
                  <th>วันเวลาที่จอง</th>
                  <th>ผู้จอง</th>
                  <th>เบอร์โทรศัพท์</th>
                  <th className="num">จำนวนคน</th>
                  <th>บริการที่ขอ</th>
                  <th>หมอนวด</th>
                  <th>หมายเหตุ</th>
                  <th>สถานะ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td><b>{r.booking_code}</b></td>
                    <td>{dateOf(r.scheduled_at)} {timeOf(r.scheduled_at)} น.</td>
                    <td>{r.booker_name || '-'}</td>
                    <td>{r.booker_phone || '-'}</td>
                    <td className="num">{r.party_size}</td>
                    <td>
                      {r.items.length === 0 ? '-' : r.items.map((i, n) => (
                        <div key={n}>
                          {i.serviceNameTh || i.serviceNameEn} · {i.durationMinutes} นาที ·{' '}
                          {money(i.price)} ฿
                        </div>
                      ))}
                    </td>
                    {/* A request with no appointment is holding a หมอนวด and a
                        bed on the booking site without anyone being given the
                        job — which is what this column is here to catch. */}
                    <td>
                      {r.status === 'pending_review' || r.status === 'cancelled' ? '-'
                        : r.assigned_count >= r.party_size
                          ? <span className="badge badge-green">จัดแล้ว</span>
                          : (
                            <span className="badge badge-purple">
                              รอจัดหมอนวด
                              {r.assigned_count > 0 ? ` · ${r.assigned_count}/${r.party_size}` : ''}
                            </span>
                          )}
                    </td>
                    <td>{r.note || '-'}</td>
                    <td>
                      <span className={`badge ${BADGE[r.status] || ''}`}>
                        {r.status === 'pending_review' ? 'กำลังชำระเงิน' : th(r.status)}
                      </span>
                    </td>
                    <td>
                      {r.status === 'draft' && (
                        <button
                          className="btn btn-solid"
                          onClick={() => setAssigning(r)}
                        >
                          ยืนยัน
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {assigning && (
        <AssignDialog
          request={assigning}
          onClose={() => setAssigning(null)}
          onDone={() => {
            setAssigning(null);
            list.reload();
            window.dispatchEvent(new Event('saanti:online-requests-changed'));
          }}
        />
      )}
    </>
  );
}

/**
 * One card per guest, holding the services that guest asked for.
 *
 * The booking site sends one row for the whole party (guestNo null) and means
 * it per head — it holds a หมอนวด and a bed for each of them — so a party-wide
 * row becomes one line each rather than one line shared.
 */
const buildGuests = (request) => Array.from({ length: request.party_size }, (_, i) => {
  const guestNo = i + 1;
  const lines = (request.items || [])
    .filter((x) => x.guestNo == null || Number(x.guestNo) === guestNo)
    .flatMap((x) => Array.from(
      { length: x.guestNo == null ? 1 : Math.max(1, Number(x.quantity) || 1) },
      () => ({
        serviceVariantId: x.serviceVariantId,
        name: x.serviceNameTh || x.serviceNameEn,
        durationMinutes: x.durationMinutes,
        therapistCount: Number(x.therapistCount ?? 1),
        requiresBed: x.requiresBed !== false,
        therapistIds: [],
        bedId: '',
      })
    ));
  return { guestNo, lines };
});

const lineDone = (l) =>
  l.therapistIds.length === l.therapistCount && (!l.requiresBed || Boolean(l.bedId));

/**
 * จัดหมอนวดและเตียง — the step ยืนยัน now goes through.
 *
 * Confirming used to be a status flip, which left a booked party with nobody to
 * do the massage and nowhere to do it. It is the same job the จองเลย builder
 * does for a walk-in, so it uses the builder's own pickers and the same
 * availability rules: only หมอนวด rostered that day and free for the window,
 * only beds free for it and able to host the service.
 */
function AssignDialog({ request, onClose, onDone }) {
  const [guests, setGuests] = useState(() => buildGuests(request));
  const [active, setActive] = useState(0);
  const [step, setStep] = useState('therapist');
  const [gender, setGender] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Treatments belonging to one guest are performed in their selected order.
  // Different guests start together, so the party ends when its longest guest
  // journey ends—not when its longest individual service ends.
  const minutes = Math.max(
    0,
    ...Array.from({ length: request.party_size }, (_, index) => {
      const guestNo = index + 1;
      return (request.items || [])
        .filter((item) => item.guestNo == null || Number(item.guestNo) === guestNo)
        .reduce((sum, item) => sum + Number(item.durationMinutes || 0), 0);
    })
  ) || 60;

  const guest = guests[active];
  const therapistTarget = guest.lines.findIndex(
    (line) => line.therapistIds.length < line.therapistCount
  );
  const bedTarget = guest.lines.findIndex((line) => line.requiresBed && !line.bedId);
  const targetIndex = step === 'therapist' ? therapistTarget : bedTarget;
  const safeTarget = targetIndex === -1 ? 0 : targetIndex;
  const targetLine = guest.lines[safeTarget];
  const targetOffset = guest.lines.slice(0, safeTarget)
    .reduce((sum, line) => sum + Number(line.durationMinutes || 0), 0);
  const targetAt = new Date(
    new Date(request.scheduled_at).getTime() + targetOffset * 60_000
  ).toISOString();
  const targetMinutes = Number(targetLine?.durationMinutes || 60);
  const bedVariantId = targetLine?.serviceVariantId || null;

  const therapists = useLoader(
    () => calendarApi.therapists(targetAt, targetMinutes),
    [request.id, active, safeTarget, targetAt, targetMinutes]
  );
  const beds = useLoader(
    () => calendarApi.beds(bedVariantId, { at: targetAt, minutes: targetMinutes }),
    [bedVariantId, targetAt, targetMinutes]
  );

  const setLine = (li, patch) =>
    setGuests((g) => g.map((x, n) => (n === active
      ? { ...x, lines: x.lines.map((l, m) => (m === li ? { ...l, ...patch } : l)) }
      : x)));

  const pickTherapist = (t, onDuty) => {
    const id = Number(t.id ?? t.therapistId);
    const commit = () => {
      const lineIndex = therapistTarget === -1 ? safeTarget : therapistTarget;
      const line = guest.lines[lineIndex];
      if (line.therapistIds.includes(id)) {
        return setLine(lineIndex, {
          therapistIds: line.therapistIds.filter((x) => x !== id),
        });
      }
      return setLine(lineIndex, { therapistIds: [...line.therapistIds, id] });
    };
    // Choosing someone who has not clocked in needs confirming, as on the real
    // board — it is easy to assign a therapist who is not actually here.
    if (!onDuty) setConfirm({ commit });
    else commit();
  };

  const pickBed = (b) => {
    const lineIndex = bedTarget === -1 ? safeTarget : bedTarget;
    if (String(guest.lines[lineIndex]?.bedId) === String(b.id)) {
      return setLine(lineIndex, { bedId: '' });
    }
    return setLine(lineIndex, { bedId: b.id });
  };

  const ready = guests.every((g) => g.lines.length > 0 && g.lines.every(lineDone));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await bookingApi.confirmRequest(request.id, guests.flatMap((g) => g.lines.map((l) => ({
        guestNo: g.guestNo,
        serviceVariantId: l.serviceVariantId,
        bedId: l.bedId || undefined,
        therapistIds: l.therapistIds,
      }))));
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  const roster = therapists.data?.therapists || [];
  const pickedTherapists = targetLine?.therapistIds || [];
  const pickedBeds = targetLine?.bedId ? [Number(targetLine.bedId)] : [];

  // Nothing is written until ยืนยัน, so the server still calls a หมอนวด and a
  // bed free when another guest on this same screen has already been given
  // them. Only the dialog knows, and if it does not say so the whole confirm
  // comes back as a double-booking the shop cannot see the cause of.
  const others = guests.filter((_, gi) => gi !== active);
  const takenTherapists = others.flatMap((g) => g.lines.flatMap((l) => l.therapistIds));
  const takenBeds = others.flatMap((g) => g.lines.map((l) => Number(l.bedId)).filter(Boolean));
  return (
    <Modal
      title={`จัดหมอนวดและเตียง · ${request.booking_code}`}
      className="assign"
      onClose={onClose}
      footer={(
        <>
          <button className="btn btn-outline" onClick={onClose}>ยกเลิก</button>
          <button className="btn btn-solid" disabled={!ready || busy} onClick={submit}>
            {busy ? 'กำลังยืนยัน…' : 'ยืนยันการจอง'}
          </button>
        </>
      )}
    >
      <div className="builder-when">
        <IconCalendar size={16} />
        <span>
          {dateOf(request.scheduled_at)} ({timeOf(request.scheduled_at)} น. · {minutes} นาที) ·{' '}
          {request.booker_name || 'ไม่ระบุชื่อ'} · {request.party_size} คน
        </span>
      </div>

      <ErrorNote error={error} />

      <div className="assign-panes">
        <aside className="assign-pick">
          <div className="pick-banner">เลือกสำหรับลูกค้าคนที่ {active + 1}</div>
          <div className="tab-row" style={{ marginBottom: 12 }}>
            <button
              className={`tab${step === 'therapist' ? ' active' : ''}`}
              onClick={() => setStep('therapist')}
            >
              หมอนวด
            </button>
            <button
              className={`tab${step === 'bed' ? ' active' : ''}`}
              onClick={() => setStep('bed')}
            >
              เตียง
            </button>
          </div>

          {therapists.loading || beds.loading ? <Spinner />
            : step === 'therapist' ? (
              <TherapistPicker
                selectedIds={pickedTherapists}
                lanes={roster.filter((t) => t.onDuty)}
                all={roster}
                availability={roster}
                blockedIds={takenTherapists}
                gender={gender}
                onGender={setGender}
                onPick={pickTherapist}
              />
            ) : (
              <BedPicker
                beds={beds.data?.beds || []}
                selectedIds={pickedBeds}
                blockedIds={takenBeds}
                onPick={pickBed}
              />
            )}
        </aside>

        <div className="assign-guests">
          {guests.map((g, gi) => (
            <div
              className={`guest-card${active === gi ? ' active' : ''}`}
              key={g.guestNo}
              onClick={() => setActive(gi)}
            >
              <div className="guest-no">{g.guestNo}</div>
              <div className="guest-body">
                <div className="slot-row">
                  <div className="slot service">
                    {g.lines.length === 0 ? (
                      <div className="slot-empty">
                        <strong>บริการ</strong>
                        <span>ไม่ได้ระบุบริการมา</span>
                      </div>
                    ) : g.lines.map((l, li) => (
                      <div className="line-row" key={li}>
                        <div className="line-name">
                          <strong>{l.name}</strong>
                          <span>
                            {l.durationMinutes} นาที
                            {l.therapistCount > 1 ? ` · หมอนวด ${l.therapistCount} คน` : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className={`slot pickable${step === 'therapist' ? ' on' : ''}`}
                    onClick={() => setStep('therapist')}
                  >
                    {g.lines.map((l, li) => (l.therapistIds.length === 0 ? (
                      <div className="slot-ghost" key={li}>
                        <IconGuest size={20} /><span>หมอนวด</span>
                      </div>
                    ) : (
                      <div className="slot-filled" key={li}>
                        <strong>
                          {l.therapistIds.map((id) => {
                            const t = roster.find((x) => x.id === id);
                            return t?.nicknameEn || t?.nicknameTh || t?.code;
                          }).join(' + ')}
                        </strong>
                        {l.therapistIds.length < l.therapistCount && (
                          <span className="assign-need">
                            ขาดอีก {l.therapistCount - l.therapistIds.length}
                          </span>
                        )}
                      </div>
                    )))}
                  </div>

                  <div
                    className={`slot pickable${step === 'bed' ? ' on' : ''}`}
                    onClick={() => setStep('bed')}
                  >
                    {g.lines.map((l, li) => {
                      if (!l.bedId) {
                        return (
                          <div className="slot-ghost" key={li}>
                            <IconPin size={20} /><span>เตียง</span>
                          </div>
                        );
                      }
                      const bed = (beds.data?.beds || []).find((b) => String(b.id) === String(l.bedId));
                      return <div className="slot-filled" key={li}><strong>{bed?.code || '—'}</strong></div>;
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {confirm && (
        <div className="modal-backdrop" onMouseDown={(e) => {
          if (e.target === e.currentTarget) setConfirm(null);
        }}>
          <div className="modal confirm">
            <div className="modal-head">
              <span />
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              หมอนวดยังไม่เริ่มงาน คุณยืนยันเลือกหมอนวดคนนี้หรือไม่?
            </div>
            <div className="modal-foot" style={{ justifyContent: 'center' }}>
              <button className="btn btn-outline" onClick={() => setConfirm(null)}>ยกเลิก</button>
              <button
                className="btn btn-solid"
                onClick={() => { confirm.commit(); setConfirm(null); }}
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
